import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, X, FloppyDisk, Palette } from "@phosphor-icons/react";
import { useAuth } from "@/lib/AuthContext";
import { Link } from "react-router-dom";

const COVER_STYLES = [
  { key: "minimal", label: "Minimal", desc: "Clean logo + title stack, generous whitespace." },
  { key: "banner", label: "Banner", desc: "Oversized title, prominent hierarchy." },
  { key: "left_bar", label: "Left bar", desc: "Vertical accent bar left of the title block." },
];

export default function BrandingPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");

  const [b, setB] = useState(null);
  const [saved, setSaved] = useState(null); // last-saved snapshot for dirty check
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/branding");
      setB(data);
      setSaved(JSON.stringify(data));
    }
    catch (e) { toast.error("Failed to load branding"); }
  };
  useEffect(() => { load(); }, []);

  const dirty = b && saved && JSON.stringify(b) !== saved;

  const set = (k, v) => setB(prev => ({ ...prev, [k]: v }));

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Logo must be under 500 KB");
    const reader = new FileReader();
    reader.onload = () => {
      set("logo_data_uri", reader.result);
      toast.success("Logo loaded. Click 'Save changes' to persist.");
    };
    reader.onerror = () => toast.error("Failed to read image");
    reader.readAsDataURL(file);
    e.target.value = ""; // allow re-uploading the same file
  };

  const onSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 300 * 1024) return toast.error("Signature must be under 300 KB");
    const reader = new FileReader();
    reader.onload = () => {
      set("signature_data_uri", reader.result);
      toast.success("Signature loaded. Click 'Save changes' to persist.");
    };
    reader.onerror = () => toast.error("Failed to read image");
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/branding", b);
      setB(data);
      setSaved(JSON.stringify(data));
      toast.success("Branding saved. Future exports will use these settings.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!b) return <div className="p-8 text-sm text-slate-500">Loading branding…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]" data-testid="branding-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Admin</div>
          <h1 className="font-display text-3xl font-black mt-1">Branding & Layout</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">These settings apply to every proposal you export. Upload your logo, pick colours, and choose a cover layout.</p>
          {!isAdmin && <p className="text-xs text-amber-700 mt-2">You are viewing in read-only mode. Only admins can save changes.</p>}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3">
            {dirty && (
              <span data-testid="branding-dirty-indicator" className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"/> Unsaved changes
              </span>
            )}
            <button onClick={save} disabled={saving || !dirty} data-testid="branding-save" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed">
              <FloppyDisk size={14} weight="fill"/> {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-6 mt-8">
        {/* form */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-display font-bold text-lg">Identity</h3>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <label className="block"><span className="label-micro">Company name</span>
                <input value={b.company_name} onChange={e => set("company_name", e.target.value)} data-testid="brand-company"
                       disabled={!isAdmin} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
              <label className="block"><span className="label-micro">Product name</span>
                <input value={b.product_name} onChange={e => set("product_name", e.target.value)} data-testid="brand-product"
                       disabled={!isAdmin} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
              <label className="block col-span-2"><span className="label-micro">Cover title (appears at the top of every export)</span>
                <input value={b.cover_title} onChange={e => set("cover_title", e.target.value)} data-testid="brand-cover-title"
                       disabled={!isAdmin} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
              <label className="block col-span-2"><span className="label-micro">Footer text (appears at the bottom of every page)</span>
                <input value={b.footer_text} onChange={e => set("footer_text", e.target.value)} data-testid="brand-footer"
                       disabled={!isAdmin} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-display font-bold text-lg">Logo</h3>
            <div className="mt-4 flex items-center gap-6">
              <div className="w-40 h-24 border-2 border-dashed border-slate-300 flex items-center justify-center bg-white rounded-sm overflow-hidden relative" data-testid="brand-logo-preview">
                {b.logo_data_uri ? (
                  <>
                    <img src={b.logo_data_uri} alt="Logo" className="max-h-full max-w-full object-contain p-2" data-testid="brand-logo-img"/>
                    <span className="absolute top-1 right-1 text-[9px] font-semibold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">Loaded</span>
                  </>
                ) : (
                  <span className="text-xs text-slate-400">No logo</span>
                )}
              </div>
              {isAdmin && (
                <div>
                  <label className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold px-4 py-2 rounded-sm cursor-pointer transition-colors duration-200">
                    <UploadSimple size={14}/> Upload logo
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onLogoUpload} className="hidden" data-testid="brand-logo-input"/>
                  </label>
                  {b.logo_data_uri && (
                    <button onClick={() => set("logo_data_uri", "")} className="ml-2 text-xs text-red-600 hover:underline" data-testid="brand-logo-clear">Remove</button>
                  )}
                  <p className="text-xs text-slate-500 mt-2">PNG, JPG, or SVG. Max 500 KB. Transparent PNG works best.</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Palette size={18}/>Colours</h3>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <span className="label-micro">Primary (headings, table headers)</span>
                <div className="mt-2 flex items-center gap-2">
                  <input type="color" value={b.primary_color} onChange={e => set("primary_color", e.target.value)} disabled={!isAdmin} className="h-10 w-14 rounded-sm border border-slate-300 cursor-pointer disabled:opacity-50" data-testid="brand-primary"/>
                  <input value={b.primary_color} onChange={e => set("primary_color", e.target.value)} disabled={!isAdmin} className="flex-1 border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono disabled:bg-slate-50"/>
                </div>
              </div>
              <div>
                <span className="label-micro">Accent (company name, highlights)</span>
                <div className="mt-2 flex items-center gap-2">
                  <input type="color" value={b.accent_color} onChange={e => set("accent_color", e.target.value)} disabled={!isAdmin} className="h-10 w-14 rounded-sm border border-slate-300 cursor-pointer disabled:opacity-50" data-testid="brand-accent"/>
                  <input value={b.accent_color} onChange={e => set("accent_color", e.target.value)} disabled={!isAdmin} className="flex-1 border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono disabled:bg-slate-50"/>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6">
            <h3 className="font-display font-bold text-lg">Signatory (appears in the Signatures section of every proposal)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <label className="block"><span className="label-micro">Signatory name</span>
                <input value={b.signatory_name || ""} onChange={e => set("signatory_name", e.target.value)} data-testid="brand-sig-name"
                       disabled={!isAdmin} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
              <label className="block"><span className="label-micro">Signatory designation</span>
                <input value={b.signatory_title || ""} onChange={e => set("signatory_title", e.target.value)} data-testid="brand-sig-title"
                       disabled={!isAdmin} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
              <label className="block sm:col-span-2"><span className="label-micro">Signatory email (optional)</span>
                <input value={b.signatory_email || ""} onChange={e => set("signatory_email", e.target.value)} data-testid="brand-sig-email" type="email"
                       disabled={!isAdmin} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
            </div>
            <div className="mt-4">
              <span className="label-micro">Signature image (optional PNG with transparent background)</span>
              <div className="mt-2 flex items-center gap-6 flex-wrap">
                <div className="w-40 h-16 border-2 border-dashed border-slate-300 flex items-center justify-center bg-white rounded-sm overflow-hidden relative" data-testid="brand-sig-preview">
                  {b.signature_data_uri ? (
                    <>
                      <img src={b.signature_data_uri} alt="Signature" className="max-h-full max-w-full object-contain p-2" data-testid="brand-sig-img"/>
                      <span className="absolute top-1 right-1 text-[9px] font-semibold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">Loaded</span>
                    </>
                  ) : (
                    <span className="text-xs text-slate-400">No signature</span>
                  )}
                </div>
                {isAdmin && (
                  <div>
                    <label className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white text-sm font-semibold px-4 py-2 rounded-sm cursor-pointer transition-colors duration-200">
                      <UploadSimple size={14}/> Upload signature
                      <input type="file" accept="image/png,image/jpeg" onChange={onSignatureUpload} className="hidden" data-testid="brand-sig-input"/>
                    </label>
                    {b.signature_data_uri && (
                      <button onClick={() => set("signature_data_uri", "")} className="ml-2 text-xs text-red-600 hover:underline" data-testid="brand-sig-clear">Remove</button>
                    )}
                    <p className="text-xs text-slate-500 mt-2">Under 300 KB. Leave blank to keep a signature line for wet-ink signing.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6">
            <div className="mt-4 grid grid-cols-3 gap-3">
              {COVER_STYLES.map(cs => (
                <button key={cs.key} onClick={() => isAdmin && set("cover_style", cs.key)} disabled={!isAdmin}
                        data-testid={`brand-cover-${cs.key}`}
                        className={`text-left p-4 border rounded-sm transition-colors ${b.cover_style === cs.key ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900" : "border-slate-200 hover:border-slate-400"}`}>
                  <div className="font-semibold">{cs.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{cs.desc}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* live preview */}
        <aside className="space-y-3">
          <div className="label-micro">Live cover preview</div>
          <div className="bg-white border border-slate-200 p-6 shadow-sm aspect-[1/1.414]" data-testid="brand-live-preview">
            {b.cover_style === "left_bar" && <div className="absolute inline-block w-1 h-24" style={{ backgroundColor: b.accent_color, marginRight: 12 }}></div>}
            <div className="flex items-start" style={{gap: 12}}>
              {b.cover_style === "left_bar" && <div className="w-1 self-stretch" style={{ backgroundColor: b.accent_color, minHeight: 60 }}/>}
              <div>
                {b.logo_data_uri ? (
                  <img src={b.logo_data_uri} alt="Logo" className="h-10 object-contain mb-3"/>
                ) : (
                  <div className="text-xs text-slate-400 mb-3">[Your logo here]</div>
                )}
                <div className="font-bold text-[11px] tracking-wider uppercase" style={{ color: b.accent_color }}>{b.company_name}</div>
                <h2 className="font-display font-black leading-tight mt-1" style={{ color: b.primary_color, fontSize: b.cover_style === "banner" ? 24 : 18 }}>
                  {b.cover_title}
                </h2>
                <div className="mt-4 text-xs text-slate-700 space-y-1">
                  <div><b>Prepared for:</b> [Client Name]</div>
                  <div><b>Recipient:</b> [Name], [Designation]</div>
                  <div><b>Issue date:</b> {new Date().toISOString().slice(0,10)}</div>
                </div>
                <div className="mt-6">
                  <div className="font-bold text-sm" style={{ color: b.primary_color }}>1. Executive Summary</div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">Lorem ipsum dolor sit amet, this is where your executive summary paragraph will appear in every exported proposal…</p>
                </div>
              </div>
            </div>
            <div className="absolute bottom-8 left-8 right-8 pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] italic text-slate-500">
              <span>{b.footer_text || "—"}</span>
              <span>Page 1</span>
            </div>
          </div>
          <p className="text-xs text-slate-500">Preview is approximate. Open any proposal → <b>Preview</b> to see how a real document will look.</p>
          <Link to="/" className="text-xs text-blue-700 underline">← Back to dashboard</Link>
        </aside>
      </div>
    </div>
  );
}
